import React from "react";
import { Button } from "@/components/ui/button";
import { Edit, Trash2 } from "lucide-react";
import { formatNumberToDisplay } from "@/lib/api/common";
import { MarketZoneBreakdown } from "@/lib/api/risk/marketZoneBreakthrough";
import { type DataTableColumn } from "@/components/ui/DataTable/index";
import { sumDecimal } from "@/components/ui/DataTable/aggregation";
import { Commodities } from "@/lib/api/commodity";
import moment from "moment-timezone";
import { CommonDestinationTimezone } from "@/lib/utils";

export interface MarketZoneBreakdownWithDisplay extends MarketZoneBreakdown {
  customer_name?: string;
  market_zone_name?: string;
}

export const createMarketZoneBreakdownColumns = ({
  showCustomerColumn = false,
  commodity,
  onEdit,
  onDelete,
}: {
  commodity?: Commodities;
  showCustomerColumn?: boolean;
  onEdit?: (breakdown: MarketZoneBreakdown) => void;
  onDelete?: (breakdown: MarketZoneBreakdown) => void;
}): DataTableColumn<MarketZoneBreakdownWithDisplay>[] => {
  const columns: DataTableColumn<MarketZoneBreakdownWithDisplay>[] = [];

  // Customer column (only when showing all customers)
  columns.push({
    id: "customer_id",
    accessorKey: "customer_id",
    header: "Customer",
    accessorFn: (row: any) => row.Customer?.long_name,
    cell: (info) => info.getValue() || "Unknown Customer",
    minSize: 150,
    size: 200,
    maxSize: 250,
    enableGrouping: true,
    enableSorting: true,
  });

  // Market Zone column
  columns.push({
    id: "market_zone_id",
    accessorKey: "market_zone_id",
    header: "Market Zone",
    accessorFn: (row: any) => row.MarketZone?.dtn_market_zone,
    cell: (info) => info.getValue() || "Unknown Market Zone",
    minSize: 120,
    maxSize: 180,
    enableSorting: true,
  });

  // Total column
  columns.push({
    id: "total",
    accessorKey: "total",
    header: "Total",
    cell: (info) => formatNumberToDisplay(info.getValue() as any, {
      passthroughEmpty: false,
      emptyFormat: "0",
      maximumFractionDigits: 0
    }),
    minSize: 100,
    maxSize: 120,
    enableSorting: false,
    enableFiltering: false,
    aggregationFn: sumDecimal,
    aggregatedCell: ({ getValue }) =>
      formatNumberToDisplay(getValue() as any, {
        passthroughEmpty: false,
        emptyFormat: "0",
        maximumFractionDigits: 0
      }),
  });

  const monthFields = [
    "month_one", "month_two", "month_three", "month_four",
    "month_five", "month_six", "month_seven", "month_eight",
    "month_nine", "month_ten", "month_eleven", "month_twelve"
  ] as const;

  if (commodity) {
    const { from } = getProjectedCropYearMap(commodity);
    for (let index = 0; index < monthFields.length; index++) {
      columns.push({
        id: monthFields[index],
        accessorKey: monthFields[index],
        header: moment.tz(from, CommonDestinationTimezone).add(index, "months").format("MMM"),
        cell: (info) => formatNumberToDisplay(info.getValue() as any, {
          passthroughEmpty: false,
          emptyFormat: "0",
          maximumFractionDigits: 0
        }),
        minSize: 80,
        maxSize: 100,
        enableSorting: false,
        enableFiltering: false,
        aggregationFn: sumDecimal,
        aggregatedCell: ({ getValue }) =>
          formatNumberToDisplay(getValue() as any, {
            passthroughEmpty: false,
            emptyFormat: "0",
            maximumFractionDigits: 0
          }),
      })
    }
  }

  // Note column
  columns.push({
    id: "note",
    accessorKey: "note",
    header: "Note",
    enableSorting: false,
    enableFiltering: false,
    cell: (info) => info.getValue() || "",
    minSize: 150,
    maxSize: 300,
  });

  // Actions column
  if (onEdit || onDelete) {
    columns.push({
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const rowData = row.original;
        return (
          <div className="flex gap-2">
            {onDelete && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onDelete(rowData)}
                className="h-8 w-8 p-0"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        );
      },
      minSize: 120,
      maxSize: 120,
      enableSorting: false,
      enableGrouping: false,
      disableRowClick: true
    });
  }

  return columns;
};

// currently you have the date windows stacked from jan-dec a typical calendar year, but we actually should adjust them for the projected crop year ie wheat would aug 1- july 31, corn oct 1- sep 30, beans sep 1- aug 31, canola aug 1- july 31
// and on example here for a real world example on crop vs calendar, i have customer with beans in the bin now that wants to move some in dec of this year and jan of next year, when i go put in teh bushels in the dec it shows for dec of 2026 rather than dec of 2025
export function getProjectedCropYearMap(commodity: Commodities, crop_year: number = new Date().getFullYear()) {
  switch (commodity) {
    case Commodities.MinneapolisWheat:
    case Commodities.WinterWheat:
    case Commodities.WinterWheatPrelimCommoditySymbol:
      return {
        from: moment.tz(`${crop_year}-08-01`, "YYYY-MM-DD", CommonDestinationTimezone).toDate(),
        to: moment.tz(`${crop_year}-07-31`, "YYYY-MM-DD", CommonDestinationTimezone).toDate()
      }
    case Commodities.Soybeans:
      return {
        from: moment.tz(`${crop_year}-09-01`, "YYYY-MM-DD", CommonDestinationTimezone).toDate(),
        to: moment.tz(`${crop_year}-08-31`, "YYYY-MM-DD", CommonDestinationTimezone).toDate()
      }
    case Commodities.Canola:
      return {
        from: moment.tz(`${crop_year}-08-01`, "YYYY-MM-DD", CommonDestinationTimezone).toDate(),
        to: moment.tz(`${crop_year}-07-31`, "YYYY-MM-DD", CommonDestinationTimezone).toDate()
      }
    case Commodities.Corn:
      return {
        from: moment.tz(`${crop_year}-10-01`, "YYYY-MM-DD", CommonDestinationTimezone).toDate(),
        to: moment.tz(`${crop_year}-09-30`, "YYYY-MM-DD", CommonDestinationTimezone).toDate()
      }
    default:
      return {};
  }
}
